import { useCallback } from 'react';
import {
  askLuna,
  getLunaMessages,
  saveLunaMessage,
  createLunaConversation,
  getLunaConversations,
  getLunaConversation,
  updateLunaConversation,
  deleteLunaConversation
} from '../../api/communication/luna';

/**
 * Hook for interacting with Luna (dashboard assistant)
 * @returns {Object} Functions to ask Luna questions and manage conversations
 */
export const useLuna = () => {
  const askLunaFunction = useCallback(async (message, currentPath, conversationId = null) => {
    console.log('[useLuna] Hook called with:', { message, currentPath, conversationId });

    try {
      console.log('[useLuna] Calling API...');
      const response = await askLuna(message, currentPath, conversationId);

      console.log('[useLuna] Raw API response:', response);
      console.log('[useLuna] Response data:', response?.data);
      console.log('[useLuna] Response structure:', {
        hasData: !!response?.data,
        success: response?.data?.success,
        hasDataData: !!response?.data?.data,
        reply: response?.data?.data?.reply?.substring(0, 100),
        navigateTo: response?.data?.data?.navigateTo,
      });

      // Axios returns response.data, so we return it directly
      const responseData = response?.data || response;

      console.log('[useLuna] Returning:', responseData);
      return responseData;
    } catch (error) {
      console.error('[useLuna] Error in hook:', error);
      console.error('[useLuna] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        url: error?.config?.url,
        method: error?.config?.method,
      });
      throw error;
    }
  }, []);

  const fetchMessages = useCallback(async (limit = 50) => {
    console.log('[useLuna] Fetching messages with limit:', limit);

    try {
      console.log('[useLuna] Calling getLunaMessages API...');
      const response = await getLunaMessages(limit);

      console.log('[useLuna] Raw API response:', response);
      console.log('[useLuna] Response data:', response?.data);

      // Axios returns response.data, so we return it directly
      const responseData = response?.data || response;

      console.log('[useLuna] Returning messages:', responseData);
      return responseData;
    } catch (error) {
      console.error('[useLuna] Error fetching messages:', error);
      console.error('[useLuna] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        url: error?.config?.url,
        method: error?.config?.method,
      });
      throw error;
    }
  }, []);

  const saveMessage = useCallback(async (message, currentPath, role = 'user', conversationId = null) => {
    console.log('[useLuna] Saving message:', { message, currentPath, role, conversationId });

    try {
      console.log('[useLuna] Calling saveLunaMessage API...');
      const response = await saveLunaMessage(message, currentPath, role, conversationId);

      console.log('[useLuna] Message saved:', response?.data);
      return response?.data || response;
    } catch (error) {
      console.error('[useLuna] Error saving message:', error);
      console.error('[useLuna] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        url: error?.config?.url,
        method: error?.config?.method,
      });
      throw error;
    }
  }, []);

  // ============================================
  // CONVERSATION HOOKS
  // ============================================

  const createConversation = useCallback(async (title = null) => {
    console.log('[useLuna] Creating conversation:', { title });

    try {
      const response = await createLunaConversation(title);
      console.log('[useLuna] Conversation created:', response?.data?.data?._id);
      return response?.data || response;
    } catch (error) {
      console.error('[useLuna] Error creating conversation:', error);
      throw error;
    }
  }, []);

  const fetchConversations = useCallback(async (limit = 20, skip = 0) => {
    console.log('[useLuna] Fetching conversations:', { limit, skip });

    try {
      const response = await getLunaConversations(limit, skip);
      console.log('[useLuna] Conversations fetched:', response?.data?.data?.length || 0);
      return response?.data || response;
    } catch (error) {
      console.error('[useLuna] Error fetching conversations:', error);
      throw error;
    }
  }, []);

  const fetchConversation = useCallback(async (conversationId, messageLimit = 100) => {
    console.log('[useLuna] Fetching conversation:', { conversationId, messageLimit });

    try {
      const response = await getLunaConversation(conversationId, messageLimit);
      console.log('[useLuna] Conversation fetched with messages:', response?.data?.data?.messages?.length || 0);
      return response?.data || response;
    } catch (error) {
      console.error('[useLuna] Error fetching conversation:', error);
      throw error;
    }
  }, []);

  const updateConversationTitle = useCallback(async (conversationId, title) => {
    console.log('[useLuna] Updating conversation title:', { conversationId, title });

    try {
      const response = await updateLunaConversation(conversationId, title);
      console.log('[useLuna] Conversation updated:', conversationId);
      return response?.data || response;
    } catch (error) {
      console.error('[useLuna] Error updating conversation:', error);
      throw error;
    }
  }, []);

  const deleteConversation = useCallback(async (conversationId) => {
    console.log('[useLuna] Deleting conversation:', conversationId);

    try {
      const response = await deleteLunaConversation(conversationId);
      console.log('[useLuna] Conversation deleted:', conversationId);
      return response?.data || response;
    } catch (error) {
      console.error('[useLuna] Error deleting conversation:', error);
      throw error;
    }
  }, []);

  return {
    askLuna: askLunaFunction,
    fetchMessages,
    saveMessage,
    // Conversation functions
    createConversation,
    fetchConversations,
    fetchConversation,
    updateConversationTitle,
    deleteConversation,
  };
};

